import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useOptimistic} from 'helpers/hooks/utils';
import StyledCollectionEntitySectionPanel
  from 'components/organisms/SectionPanels/CollectionEntitySectionPanel/CollectionEntitySectionPanel.styles';
import {useProfile} from 'components/organisms/Providers/ProfileProvider/ProfileProvider';
import ActionButton from 'components/molecules/Buttons/ActionButton/ActionButton';
import {useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import Delete from '@mui/icons-material/Delete';
import Add from '@mui/icons-material/Add';
import RelevancyContextCard from 'components/organisms/Cards/RelevancyContextCard/RelevancyContextCard';
import {
  useEntityCsi,
  useEntityPersonalRelevancy,
  useEntityExternalRelevancy
} from 'services/entity/entity.utils';
import DealflowStatusContextCard from 'components/organisms/Cards/DealflowStatusContextCard/DealflowStatusContextCard';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import CollectionSelectionDialog
  from 'components/organisms/Dialogs/CollectionSelectionDialog/CollectionSelectionDialog';
import EntityTimelineSectionPanelContent
  from 'components/organisms/SectionPanels/EntityTimelineSectionPanelContent/EntityTimelineSectionPanelContent';
import EntityTasksContextCard from 'components/organisms/Cards/EntityTasksContextCard/EntityTasksContextCard';
import CollectionEntityProfile from 'components/organisms/Profiles/CollectionEntityProfile/CollectionEntityProfile';
import EntityAddSectionPanelContent
  from 'components/organisms/SectionPanels/EntityAddSectionPanelContent/EntityAddSectionPanelContent';
import SectionPanelFooter from 'components/molecules/SectionPanels/SectionPanelFooter/SectionPanelFooter';
import constants from 'helpers/constants';
import {useSnackbar} from 'components/organisms/Providers/SnackbarProvider/SnackbarProvider';
import utils from 'helpers/utils';
import QuestionnaireContextCard from 'components/organisms/Cards/QuestionnaireContextCard/QuestionnaireContextCard';
import {useCollectionQuestionnaireStatsList} from 'services/collection/questionnaire/questionnaire.hooks';
import FolderSharp from '@mui/icons-material/FolderSharp';

const CollectionEntitySectionPanel = React.forwardRef((props, ref) => {
  const {
    showProfile,
    newProfile,
    onNext,
    onClose,
    onCloseCard,
    ...innerProps
  } = useComponentProps(props, 'CollectionEntitySectionPanel');

  const profileProvider = useProfile();
  const isSuggestion = !showProfile && Boolean(profileProvider?.view?.activeViews?.find((v) => v.name === 'suggestions'));
  const isRejection = !showProfile && Boolean(profileProvider?.view?.activeViews?.find((v) => v.name === 'rejections'));
  const sectionDefinitions = profileProvider?.sectionDefinitions;
  const collection = profileProvider?.context?.data;

  const questionnaireIds = (collection?.activeQuestions ?? []).reduce((a, q) => a.concat([q.questionnaireId]), []);
  const questionnaireStats = useCollectionQuestionnaireStatsList({
    collectionId: collection?.collectionId,
    questionnaireIds: utils.uniqueArray(questionnaireIds)
  }, {
    enabled: collection?.collectionId > 0 && questionnaireIds.length > 0
  });

  const fieldData = useMemo(() => ({
    ...profileProvider.fieldData,
    questionnaireStats: questionnaireStats.data
  }), [questionnaireStats.data, profileProvider.fieldData]);

  const dialogControl = useDialogControl();
  const authorize = useAuthorize();

  const snackbar = useSnackbar();
  const setSettingsEvent = useEffectEvent(profileProvider?.setSettings);
  const openTaskEvent = useEffectEvent(profileProvider.openTask);
  const updateCsiEvent = useEffectEvent(profileProvider.updaters?.updateCsi);
  const entityUnAssignEvent = useEffectEvent(profileProvider.updaters?.unAssignItems);
  const entityAssignEvent = useEffectEvent(profileProvider.updaters?.assignItems);
  const entityAcceptEvent = useEffectEvent(profileProvider.updaters?.acceptItems);
  const createAnswerEvent = useEffectEvent(profileProvider.updaters?.createAnswer);

  const onNextEvent = useEffectEvent(onNext);
  const onCloseEvent = useEffectEvent(onClose);
  const onCloseCardEvent = useEffectEvent(onCloseCard);
  const sections = useMemo(() => {
    if (sectionDefinitions) {
      const sections = [];

      sectionDefinitions
        .sort((a, b) => a.position - b.position)
        .forEach((sectionDef) => {
          const canEditSection = (entity) => {
            return !(sectionDef.readOnly ?? false) && authorize({...sectionDef.auth?.update, meta: {...sectionDef.auth?.update?.meta, collection, entity}});
          }
          const canEditCard = (card, entity) => {
            return canEditSection(entity) && !(card.readOnly ?? false) && authorize({
              ...card.auth?.update,
              meta: {
                ...card.auth?.update?.meta,
                collection, entity}
            });
          }

          if (newProfile) {
            if (sectionDef.name === 'profile') {
              sections.push({
                ...sectionDef,
                Content: () => {
                  return <EntityAddSectionPanelContent key={newProfile}
                                                       collection={collection}
                                                       onNext={onNextEvent}
                                                       onClose={onCloseEvent}/>
                }
              });
            }
          } else {
            if (sectionDef.name === 'profile' && showProfile) {
              sections.push({
                ...sectionDef,
                Content: () => {
                  return <CollectionEntityProfile gap={8} columns={1}
                                                  onCloseCard={onCloseCardEvent}
                                                  onCanUpdate={canEditSection}/>
                }
              });
            } else if (sectionDef.name === 'options') {
              sections.push({
                ...sectionDef,
                cards: sectionDef.cards?.reduce((a, card) => {
                  if (card.name === 'personalRelevancy') {
                    a.push({
                      ...card,
                      Card: ({data, isLoading}) => {
                        const relevancyValue = useEntityPersonalRelevancy(data, collection);
                        const [relevancy, toggleRelevancyOptimistic] = useOptimistic(data?.entityId, relevancyValue || 0, updateCsiEvent);

                        const handleChange = (value) => {
                          return toggleRelevancyOptimistic?.(value, data, {
                            relevancy: value,
                            collection_id: collection?.collectionId
                          }).catch((err) => {
                            snackbar.show('Saving relevance failed', null,
                                {color: 'error', autoHideDuration: constants.delay.error});

                            throw err;
                          })
                        };

                        return <RelevancyContextCard relevancy={relevancy}
                                                     onChange={handleChange}
                                                     canUpdate={canEditCard(card, data)}
                                                     isLoading={isLoading}/>
                      }
                    });
                  } else if (card.name === 'externalRelevancy') {
                    a.push({
                      ...card,
                      Card: ({data, isLoading}) => {
                        const relevancyValue = useEntityExternalRelevancy(data, collection);
                        const [relevancy, toggleRelevancyOptimistic] = useOptimistic(data?.entityId, relevancyValue || 0, updateCsiEvent);

                        const handleChange = (value) => {
                          return toggleRelevancyOptimistic?.(value, data, {
                            relevancy: value,
                            collection_id: collection?.collectionId
                          }).catch((err) => {
                            snackbar.show('Saving relevance failed', null,
                                {color: 'error', autoHideDuration: constants.delay.error});

                            throw err;
                          })
                        };

                        return <RelevancyContextCard relevancy={relevancy}
                                                     title="External relevance"
                                                     onChange={handleChange}
                                                     canUpdate={canEditCard(card, data)}
                                                     isLoading={isLoading}/>
                      }
                    });
                  } else if (card.name === 'tasks') {
                    a.push({
                      ...card,
                      Card: ({data, isLoading}) => {
                        const switched = Boolean(profileProvider?.state?.settings?.tasksSwitched);

                        const handleSwitch = (switched) => {
                          setSettingsEvent?.({tasksSwitched: switched});
                        }

                        const handleClick = (e, task) => {
                          if (!e.defaultPrevented) {
                            openTaskEvent?.(task.commentId);
                          }
                        }

                        return <EntityTasksContextCard entity={data}
                                                       collection={collection}
                                                       switched={switched}
                                                       onSwitch={handleSwitch}
                                                       onClick={handleClick}
                                                       canUpdate={canEditCard(card, data)}
                                                       isLoading={isLoading}/>
                      }
                    });
                  } else if (card.name === 'dealflowStatus') {
                    a.push({
                      ...card,
                      Card: ({data, isLoading}) => {
                        const csi = useEntityCsi(data);
                        const [statusId, toggleStatusOptimistic] = useOptimistic(data?.entityId, csi?.status || 0, updateCsiEvent);

                        const handleChange = (value) => {
                          return toggleStatusOptimistic?.(value, data, {
                            status: value,
                            collectionId: collection.collectionId
                          }).catch((err) => {
                            snackbar.show('Saving status failed', null,
                                {color: 'error', autoHideDuration: constants.delay.error});

                            throw err;
                          })
                        };

                        return <DealflowStatusContextCard statusId={statusId}
                                                          onChange={handleChange}
                                                          canUpdate={canEditCard(card, data)}
                                                          isLoading={isLoading}/>
                      }
                    });
                  }

                  return a;
                }, []),
                Footer: ({data, isLoading}) => {
                  const [{collections, removals}, entityUpdateOptimistic] = useOptimistic(data?.entityId, {collections: data?.collections, removals: []}, (collections, type) => {
                    if (type === 'accept') {
                      return Promise.all(collections.map((collection) => {
                        return entityAcceptEvent?.({
                          collectionId: collection.collectionId,
                          entityId: data.entityId
                        });
                      }));
                    } else if (type === 'remove') {
                      return Promise.all(collections.map((collection) => {
                        return entityUnAssignEvent?.({
                          collectionId: collection.collectionId,
                          entityId: data.entityId
                        });
                      }));
                    } else {
                      return Promise.all(collections.map((collection) => {
                        return entityAssignEvent?.({
                          collectionId: collection.collectionId,
                          entityId: data.entityId
                        });
                      }));
                    }
                  });

                  const isPartOfCollection = Boolean(collections?.find((c) => +c.collectionId === +collection?.collectionId));
                  const isRemovedCollection = Boolean(removals?.find((c) => +c.collectionId === +collection?.collectionId));

                  const buttonActions = useMemo(() => {
                    const actions = [];

                    if (!isRejection) {
                      actions.push({
                        auth: utils.createAuth({attribute: 'collection.update'}),
                        onClick: () => {
                          entityUpdateOptimistic?.({
                            collections: collections.filter((c) => +c.collectionId !== +collection.collectionId),
                            removals: removals.concat([collection])
                          }, [collection], 'remove')
                            .catch(() => {
                              snackbar.show(`Removing ${isSuggestion ? 'suggestion' : 'company'} from collection '${collection.name}' failed`, null,
                                {color: 'error', autoHideDuration: constants.delay.error});
                            });
                        },
                        label: isSuggestion ? 'Remove suggestion' : 'Remove from collection',
                        icon: Delete,
                        ActionButtonProps: {
                          color: 'error',
                          radius: 'round',
                          fullWidth: true
                        },
                        ButtonProps: {
                          disabled: isRemovedCollection
                        }
                      });
                    }

                    actions.push({
                      auth: utils.createAuth({attribute: 'collection.update'}),
                      onClick: () => {
                        const handleSubmit = (addedCollections) => {
                          if (addedCollections.length > 0) {
                            const message = `Adding the company to the collection${addedCollections.length === 1 ? '' : 's'}`;
                            return utils.observePromise(entityUpdateOptimistic?.({
                                collections: collections.concat(addedCollections),
                                removals: removals.filter((c) => !addedCollections.find((a) => +c.collectionId !== +a.collectionId))
                              }, addedCollections, 'assign'
                            ), () => {
                              return snackbar.show(`${message}...`, null, {
                                color: 'info',
                                autoHideDuration: null
                              });
                            }, () => {
                              return snackbar.show(`The company was added to the collection${addedCollections.length === 1 ? '' : 's'}`, null,
                                {color: 'success', autoHideDuration: constants.delay.success});
                            }, () => {
                              return snackbar.show(`${message} failed`, null,
                                {color: 'error', autoHideDuration: constants.delay.error});
                            }, (id) => {
                              snackbar.hide(id);
                            });
                          }
                        };

                        if (isSuggestion || isRejection || !isPartOfCollection) {
                          if (isSuggestion || isRejection) {
                            entityUpdateOptimistic?.({
                              collections: collections.concat([collection]),
                              removals: removals.filter((c) => +c.collectionId !== +collection.collectionId)
                            }, [collection], 'accept')
                              .catch(() => {
                                snackbar.show(`Adding ${isSuggestion ? 'suggestion' : 'company'} to collection '${collection.name}' failed`, null,
                                  {color: 'error', autoHideDuration: constants.delay.error});
                              });
                          } else {
                            entityUpdateOptimistic?.({
                              collections: collections.concat([collection]),
                              removals: removals.filter((c) => +c.collectionId !== +collection.collectionId)
                            }, [collection], 'assign')
                              .catch(() => {
                                snackbar.show(`Adding company to collection '${collection.name}' failed`, null,
                                  {color: 'error', autoHideDuration: constants.delay.error});
                              });
                          }
                        } else {
                          dialogControl.show(<CollectionSelectionDialog title="Add to collection"
                                                                        subtitle="For 1 company"
                                                                        collections={collections}
                                                                        hideCollections={true}
                                                                        onSubmit={handleSubmit}/>, true);
                        }
                      },
                      label: (isSuggestion || isRejection || !isPartOfCollection) ? 'Add to collection' : 'Add to other collection',
                      icon: (isSuggestion || isRejection || !isPartOfCollection) ? Add : FolderSharp,
                      ActionButtonProps: {
                        color: 'primary',
                        radius: 'round',
                        fullWidth: true
                      },
                      ButtonProps: {
                        disabled: (isSuggestion || isRejection) && isPartOfCollection
                      }
                    })

                    return actions;
                  }, [collections, removals, isPartOfCollection, isRemovedCollection, entityUpdateOptimistic]);

                  return <SectionPanelFooter className="CollectionEntitySectionPanel-options-footer"
                                             orientation="vertical"
                                             buttons={buttonActions.map?.((action, idx) => {
                                               return <ActionButton key={idx} action={action} isLoading={isLoading}/>
                                             })} />
                }
              });
            } else if (sectionDef.name === 'questionnaire') {
              sections.push({
                ...sectionDef,
                cards: sectionDef.cards.map((card) => {
                  const questionnaireQuestion = card;

                  return {
                    ...card,
                    Card: ({data, isLoading}) => {
                      const handleChange = (answer) => {
                        const activeQuestionnaireIds = questionnaireQuestion?.questionnaires?.filter((qn) => {
                          return data.activeQuestions?.find((q) => +q.questionId === +questionnaireQuestion.questionId && +q.questionnaireId === +qn.questionnaireId);
                        }).map((qn) => qn.questionnaireId);

                        return createAnswerEvent?.({
                          entityId: data.entityId,
                          questionId: questionnaireQuestion.questionId,
                          questionnaireIds: activeQuestionnaireIds,
                          ...answer,
                          value: utils.isArray(answer.value) ? answer.value.map((v) => v.tagId ?? v.value) :
                            answer.value.tagId ?? answer.value.value
                        })
                          .catch((err) => {
                            snackbar.show('Saving vote failed', null,
                              {color: 'error', autoHideDuration: constants.delay.error});
                            throw err;
                          });
                      };

                      const forUser = Boolean(questionnaireQuestion?.questionnaires?.find((qn) => {
                        return data.activeQuestions?.find((q) => q.forUser && +q.questionId === +questionnaireQuestion.questionId &&
                          +q.questionnaireId === +qn.questionnaireId);
                      }));

                      const canUpdate = canEditCard(card, data) && forUser;

                      return <QuestionnaireContextCard entity={data}
                                                       fieldData={fieldData}
                                                       collection={collection}
                                                       questionnaireQuestion={questionnaireQuestion}
                                                       canUpdate={canUpdate}
                                                       onChange={handleChange}
                                                       isLoading={isLoading}/>
                    },
                    hidden: ({data}) => {
                      return !questionnaireQuestion?.questionnaires?.find((qn) => {
                        return data.activeQuestions?.find((q) => +q.questionId === +questionnaireQuestion.questionId && +q.questionnaireId === +qn.questionnaireId);
                      });
                    }
                  }
                }),
                hidden: ({data}) => {
                  return !Boolean(data?.hasActiveQuestions);
                }
              });
            } else if (sectionDef.name === 'timeline') {
              sections.push({
                ...sectionDef,
                Content: ({data, isLoading}) => {
                  return <EntityTimelineSectionPanelContent collection={collection}
                                                            entity={data}
                                                            canUpdate={canEditSection(data)}
                                                            isLoading={isLoading}/>
                }
              });
            }
          }
        });

      return sections;
    } else {
      return [];
    }
  }, [authorize, dialogControl, snackbar, fieldData, sectionDefinitions, collection, showProfile, newProfile,
    isSuggestion, isRejection, profileProvider?.state?.settings?.tasksSwitched,
    setSettingsEvent, onCloseEvent, onCloseCardEvent, onNextEvent, openTaskEvent, updateCsiEvent,
    entityUnAssignEvent, entityAssignEvent, entityAcceptEvent, createAnswerEvent]);

  return <StyledCollectionEntitySectionPanel ref={ref} {...innerProps}
                                             sections={sections}
                                             onClose={onClose}
                                             onCloseCard={onCloseCard}
                                             showProfile={showProfile}
                                             newProfile={newProfile}/>
});

CollectionEntitySectionPanel.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  onClose: PropTypes.func,
  onCloseCard: PropTypes.func,
  showProfile: PropTypes.bool,
  newProfile: PropTypes.any
};

export default CollectionEntitySectionPanel;
